/**
 * Flatten an Error's `cause` chain into PostHog-filterable properties.
 *
 * Shared by both capture paths. It used to live only in `posthog-server.ts`,
 * so every browser-side `logger.error("label", { error: pgErr })` reached
 * PostHog carrying the label and nothing else — no message, code, details or
 * hint. `[useCallState] Failed to fetch active call` is the standing example:
 * 22 events in 7 days, all with `cause_messages` null and no way to tell which
 * Postgres error caused them.
 *
 * Pure and isomorphic — no server-only imports, safe in a client bundle.
 */

interface SupabaseLikeError {
  name?: string;
  message: string;
  cause?: unknown;
  code?: string;
  details?: string;
  hint?: string;
  status?: number;
}

/**
 * `instanceof Error` is the wrong test for a Supabase cause.
 *
 * @supabase/postgrest-js returns `error = JSON.parse(body)` on the
 * `{ data, error }` path — a PLAIN OBJECT. It only constructs a real
 * `PostgrestError` under `.throwOnError()`, which this codebase does not use.
 * So every `throw new Error(code, { cause: pgError })` — the pattern the repo
 * mandates and an ast-grep rule enforces — attached a POJO that the old
 * `while (current instanceof Error)` rejected on the first iteration, yielding
 * an empty chain and `{}`.
 *
 * Measured cost: all 12 `banking.bulk_confirm.row_failed` events in PostHog
 * over three days had cause_messages, cause_code, cause_details and cause_hint
 * ALL null. The discipline was paid for and returned nothing — every money
 * write failure was a content-free wrapper.
 *
 * Anything carrying a string `message` is a usable cause, Error or not.
 */
function asCause(value: unknown): SupabaseLikeError | null {
  if (typeof value !== "object" || value === null) return null;
  const message = (value as { message?: unknown }).message;
  return typeof message === "string" ? (value as SupabaseLikeError) : null;
}

export function flattenCauseChain(err: Error): Record<string, unknown> {
  const chain: SupabaseLikeError[] = [];
  let current: unknown = err.cause;
  for (let node = asCause(current); node && chain.length < 5; ) {
    chain.push(node);
    current = node.cause;
    node = asCause(current);
  }
  if (chain.length === 0) return {};

  const props: Record<string, unknown> = {
    cause_messages: chain.map((c) => c.message),
    // A plain PostgREST object has no `name`; label it by its code instead so
    // the chain string stays readable rather than printing "undefined:".
    cause_chain: chain
      .map(
        (c, i) =>
          `${String(i + 1)}. ${c.name ?? c.code ?? "Error"}: ${c.message}`,
      )
      .join(" | "),
  };

  // Surface Postgres / Supabase / fetch-Response fields from the immediate
  // cause (the most actionable one) at top-level property keys so PostHog's
  // filterable property index can pivot on them.
  // `chain` is already SupabaseLikeError[] now that asCause narrows it, so the
  // old cast here is redundant.
  const top = chain[0];
  if (top.code) props.cause_code = top.code;
  if (top.details) props.cause_details = top.details;
  if (top.hint) props.cause_hint = top.hint;
  if (typeof top.status === "number") props.cause_status = top.status;

  return props;
}
